import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import {
  ArrowLeft,
  Eye,
  Edit,
  Download,
  Upload,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  DollarSign,
  Calendar,
  User,
  Building,
  Send,
  ChevronLeft,
  ChevronRight
} from '../components/Icons';
import { toast } from 'react-hot-toast';
import { apiGet, apiPost } from '../utils/api';
import { downloadInvoicePdf } from '../utils/downloadInvoicePdf';
import { getFbrStatusColor, getFbrStatusIcon } from '../utils/fbrStatus';

const BatchInvoices = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();

  const [batchData, setBatchData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingToFbr, setSubmittingToFbr] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (batchId) {
      fetchBatchInvoices();
    }
  }, [batchId]);

  const fetchBatchInvoices = async (page = 1) => {
    try {
      setLoading(true);
      const response = await apiGet(`/api/batch/${batchId}/invoices?page=${page}&limit=${itemsPerPage}`);
      if (response.ok) {
        setBatchData(response.data);
        setInvoices(response.data.invoices || []);
        setTotalPages(Math.ceil((response.data.total_invoices || 0) / itemsPerPage));
        setCurrentPage(page);
      } else {
        if (response.status === 404) {
          toast.error('Batch not found');
        } else {
          toast.error('Failed to fetch batch invoices');
        }
        // Don't redirect immediately, let user see the error
        setBatchData(null);
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error fetching batch invoices:', error);
      toast.error('Failed to fetch batch invoices');
      // Don't redirect immediately, let user see the error
      setBatchData(null);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/invoices/${invoiceId}/view`);
  };

  const handleEditInvoice = (invoiceId) => {
    navigate(`/invoices/${invoiceId}/edit`);
  };

  const handleSubmitToFbr = async (invoiceId) => {
    try {
      setSubmittingToFbr(invoiceId);
      const response = await apiPost(`/api/invoices/${invoiceId}/submit-to-fbr`);
      if (response.ok) {
        toast.success('Invoice submitted to FBR successfully');
        fetchBatchInvoices(currentPage); // Refresh current page
      } else {
        toast.error(response.data?.detail || 'Failed to submit invoice to FBR');
      }
    } catch (error) {
      console.error('Error submitting to FBR:', error);
      toast.error('Failed to submit invoice to FBR');
    } finally {
      setSubmittingToFbr(null);
    }
  };

  // Pagination functions
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchBatchInvoices(page);
    }
  };

  const getPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          items.push(i);
        }
        items.push('...');
        items.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        items.push(1);
        items.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          items.push(i);
        }
      } else {
        items.push(1);
        items.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          items.push(i);
        }
        items.push('...');
        items.push(totalPages);
      }
    }

    return items;
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      setGeneratingPdf(invoiceId);
      await downloadInvoicePdf(invoiceId, { invoiceNumber });
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(error.message || 'Failed to download PDF');
    } finally {
      setGeneratingPdf(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Navigation>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading batch invoices...</p>
          </div>
        </div>
      </Navigation>
    );
  }

  return (
    <Navigation>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <button
                  onClick={() => navigate('/batch-management')}
                  className="mr-4 p-2 text-gray-400 hover:text-gray-600"
                  title="Back to Batch Management"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Batch Invoices</h1>
                  <p className="text-sm text-gray-600">Batch ID: {batchId}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/batch-management')}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Back to Batches
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Message */}
          {!loading && !batchData && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-3" />
                <div>
                  <h3 className="text-lg font-medium text-red-800">Batch Not Found</h3>
                  <p className="text-sm text-red-600 mt-1">
                    The batch with ID "{batchId}" could not be found or you don't have permission to view it.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Batch Summary */}
          {batchData && (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Batch Summary</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">Total Items</p>
                    <p className="text-2xl font-bold text-gray-900">{batchData.total_items}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">Successful</p>
                    <p className="text-2xl font-bold text-green-600">{batchData.successful_items}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">Failed</p>
                    <p className="text-2xl font-bold text-red-600">{batchData.failed_items}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">Batch Status</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      batchData.batch_status === 'completed' ? 'bg-green-100 text-green-800' :
                      batchData.batch_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                      batchData.batch_status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {batchData.batch_status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Invoices Table */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Invoices ({invoices.length})</h3>
            </div>

            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
                <p className="mt-1 text-sm text-gray-500">This batch doesn't contain any invoices yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        FBR Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        FBR Error
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {invoice.invoice_number}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {invoice.id}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {invoice.client?.name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {invoice.client?.ntn_cnic || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(invoice.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(invoice.total_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getFbrStatusIcon(invoice.fbr_status)}
                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFbrStatusColor(invoice.fbr_status)}`}>
                              {invoice.fbr_status}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {invoice.fbr_error ? (
                            <div className="flex items-center">
                              <AlertTriangle className="h-4 w-4 text-red-500 mr-1" />
                              <span className="text-sm text-red-600 truncate max-w-xs" title={invoice.fbr_error}>
                                {invoice.fbr_error}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">No errors</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewInvoice(invoice.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Invoice"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditInvoice(invoice.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Edit Invoice"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {invoice.fbr_status !== 'submitted' && (
                              <button
                                onClick={() => handleSubmitToFbr(invoice.id)}
                                disabled={submittingToFbr === invoice.id}
                                className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                                title="Submit to FBR"
                              >
                                {submittingToFbr === invoice.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}
                              disabled={generatingPdf === invoice.id}
                              className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                              title="Download PDF"
                            >
                              {generatingPdf === invoice.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page <span className="font-medium">{currentPage}</span> of{' '}
                        <span className="font-medium">{totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>

                        {getPaginationItems().map((item, index) => (
                          <button
                            key={index}
                            onClick={() => typeof item === 'number' ? handlePageChange(item) : null}
                            disabled={item === '...'}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              item === currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : item === '...'
                                ? 'border-gray-300 bg-white text-gray-700 cursor-default'
                                : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {item}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </Navigation>
  );
};

export default BatchInvoices;
